import type { HTMLAttributes } from "react"
import {
  FieldError as AriaFieldError,
  Input as AriaInput,
  Label as AriaLabel,
  Text as AriaText,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type TextFieldRenderProps as AriaTextFieldRenderProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { LabelAndDescriptionStack } from "./Field"

export type TextFieldProps = Omit<AriaTextFieldProps, "className"> & {
  description?: string
  errorMessage?: string
  label?: React.ReactNode
  placeholder?: string
  /** An escape hatch for any additional props you want to pass to the input. */
  inputProps?: HTMLAttributes<HTMLInputElement>
  size?: "small" | "medium" | "large"
  slots?: {
    leading?: AriaTextFieldProps["children"]
    trailing?: AriaTextFieldProps["children"]
  }
  className?: string
}

type SlotProps = HTMLAttributes<HTMLDivElement>

export const Slot = ({ children, className, ...props }: SlotProps) => (
  <div className={twMerge("tt-slot", className)} {...props}>
    {children}
  </div>
)

const makeSlot = (
  slot: AriaTextFieldProps["children"],
  state: AriaTextFieldRenderProps & {
    defaultChildren: React.ReactNode | undefined
  },
) => {
  if (typeof slot === "function") {
    return slot(state)
  } else if (slot) {
    return <Slot>{slot}</Slot>
  }

  return null
}

/**
 * A form field that allows users to input text.
 *
 * ```tsx
 * import { TextField } from "@tokenterminal/core/ui/input/TextField"
 *
 * <TextField
 *   label="Email"
 *   placeholder="Enter your email"
 *   description="We'll never share your email with anyone else."
 * />
 * ```
 */

export const TextField = ({
  description,
  errorMessage,
  inputProps,
  className,
  label,
  placeholder,
  size = "medium",
  slots = {},
  ...props
}: TextFieldProps) => {
  return (
    <AriaTextField className={twMerge("tt-field-wrapper", className)} {...props}>
      {(state) => (
        <>
          {(label || description) && (
            <LabelAndDescriptionStack>
              {label && <AriaLabel className="tt-field-label">{label}</AriaLabel>}
              {description && (
                <AriaText slot="description" className="tt-field-description">
                  {description}
                </AriaText>
              )}
            </LabelAndDescriptionStack>
          )}
          <div data-size={size} className="tt-input-container">
            {makeSlot(slots.leading, state)}
            <AriaInput className="tt-input-base" placeholder={placeholder} {...inputProps} />
            {makeSlot(slots.trailing, state)}
          </div>
          <AriaFieldError className="tt-field-error">{errorMessage}</AriaFieldError>
        </>
      )}
    </AriaTextField>
  )
}
